import React, { useEffect, useState } from "react";

//redux
import { connect, useDispatch, useSelector } from "react-redux";

//MUI
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    FormControl,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
} from "@material-ui/core";
import { Cancel, ExpandMore } from "@material-ui/icons";

//types
import { CLOSE_ADMIN_ACCESS_DIALOG } from "../../store/adminAccess/types";

//action
import {
    getAdminAccessByAdminId,
    createAdminAccess,
    updateAdminAccessByAdminId,
    updateFeatureAccess,
    updateAdminRole,
} from "../../store/adminAccess/action";

import { permissionError } from "../../util/Alert";

const ROLES = ["Admin", "SuperAdmin", "Manager"];

// Feature structure matching the backend model
const FEATURE_STRUCTURE = {
    dashboard: {
        label: "Dashboard",
        features: {
            userChart: "User Chart",
            totalUser: "Total Users",
            totalLiveUser: "Total Live Users",
            totalActiveUser: "Total Active Users",
            totalVipUser: "Total VIP Users",
            totalRevenue: "Total Revenue",
            totalPost: "Total Posts",
            totalVideo: "Total Videos",
            totalReportedUser: "Total Reported Users",
            totalUserDiamond: "Total User Diamonds",
            totalUserVCoin: "Total User VCoins",
        },
    },
    users: {
        label: "Users",
        features: {
            users: "Users List",
            bannedUsers: "Banned Users",
            bannedDevices: "Banned Devices",
            whiteListedDevices: "Whitelisted Devices",
        },
    },
    live: {
        label: "Live",
        features: {
            partyRoom: "Party Room",
            videoLive: "Video Live",
            bannedUsers: "Banned Users",
        },
    },
    plan: {
        label: "Plans",
        features: {
            diamondPlan: "Diamond Plan",
            vipPlan: "VIP Plan",
        },
    },
    planHistory: {
        label: "Plan History",
        features: {
            diamondPlan: "Diamond Plan History",
            vipPlan: "VIP Plan History",
            purchaseTransactions: "Purchase Transactions",
        },
    },
    gift: {
        label: "Gifts",
        features: {
            categories: "Gift Categories",
            allGifts: "All Gifts",
        },
    },
    store: {
        label: "Store",
        features: {
            entranceEffects: "Entrance Effects",
            avatarFrame: "Avatar Frame",
            background: "Background",
            diamondShop: "Diamond Shop",
        },
    },
    rewards: {
        label: "Rewards",
        features: {
            sendReward: "Send Reward",
            levelUpRewards: "Level Up Rewards",
            rechargeRewards: "Recharge Rewards",
            vipRewards: "VIP Rewards",
            dailyTaskRewards: "Daily Tasks",
        },
    },
    diamondSeller: {
        label: "Diamond Seller",
        features: {
            diamondSellers: "Diamond Sellers",
            diamondSellerShop: "Diamond Seller Shop",
        },
    },
    reports: {
        label: "Reports",
        features: {
            reportedUsers: "Reported Users",
            reportedMoments: "Reported Moments",
            reportedReels: "Reported Reels",
        },
    },
    complain: {
        label: "Complaints",
        features: {
            pending: "Pending Complaints",
            resolved: "Resolved Complaints",
        },
    },
    redeem: {
        label: "Redeem",
        features: {
            pending: "Pending Redeems",
            accepted: "Accepted Redeems",
            declined: "Declined Redeems",
            paymentMethods: "Payment Methods",
        },
    },
    backendManagement: {
        label: "Backend Management",
        features: {
            makeVip: "Make VIP",
            diasAppPurchase: "Dias App Purchase",
            ghostAccounts: "Ghost Accounts",
            restrictedAccounts: "Restricted Accounts",
            gameSettings: "Game Settings",
            reopenTreasureBox: "Reopen Treasure Box",
            ipManagement: "IP Management",
            suspiciousGiftPairs: "Suspicious Gift Pairs",
            faceReviews: "Liveness Face Reviews",
        },
    },
};

// Simple features (not nested)
const SIMPLE_FEATURES = {
    banner: "Banner",
    partyChallenge: "Party Challenge",
    sticker: "Sticker",
    theme: "Theme",
    arEffects: "AR Effects",
    song: "Song",
    hashtag: "Hashtag",
    level: "Level",
    post: "Post",
    reels: "Reels",
    deactivation: "Deactivation",
    googleAd: "Google Ad",
    settings: "Settings",
    adminManagement: "Admin Management",
};

const AdminAccessDialog = (props) => {
    const dispatch = useDispatch();

    const { dialog: open, dialogData, adminAccess, loading } = useSelector(
        (state) => state.adminAccess
    );
    const hasPermission = useSelector((state) => state.admin.admin.flag);

    const [role, setRole] = useState("Admin");
    const [accessData, setAccessData] = useState({});
    const [hasExistingAccess, setHasExistingAccess] = useState(false);

    // Fetch admin access when dialog opens
    useEffect(() => {
        if (dialogData && dialogData._id) {
            props.getAdminAccessByAdminId(dialogData._id);
        }
    }, [dialogData]);

    // Initialize access data when adminAccess loads
    useEffect(() => {
        if (adminAccess) {
            setHasExistingAccess(true);
            setRole(adminAccess.role || "Admin");
            setAccessData(adminAccess);
        } else if (!loading && dialogData) {
            // No existing access, set defaults
            setHasExistingAccess(false);
            setRole("Admin");
            setAccessData({});
        }
    }, [adminAccess, loading, dialogData]);

    const closePopup = () => {
        dispatch({ type: CLOSE_ADMIN_ACCESS_DIALOG });
    };

    const getNestedValue = (obj, path) => {
        const keys = path.split(".");
        let value = obj;
        for (const key of keys) {
            if (value && typeof value === "object") {
                value = value[key];
            } else {
                return 1; // Default to read-only
            }
        }
        return value !== undefined ? value : 1;
    };

    const handleFeatureChange = (featurePath, value) => {
        if (!hasPermission) return permissionError();

        const newAccessData = { ...accessData };
        const keys = featurePath.split(".");
        let current = newAccessData;

        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
                current[keys[i]] = {};
            }
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;

        setAccessData(newAccessData);
    };

    const handleRoleChange = (newRole) => {
        if (!hasPermission) return permissionError();
        setRole(newRole);
    };

    const handleCreateAccess = () => {
        if (!hasPermission) return permissionError();

        const data = {
            adminId: dialogData._id,
            role: role,
            ...accessData,
        };
        props.createAdminAccess(data);
    };

    const handleSaveAll = () => {
        if (!hasPermission) return permissionError();

        if (hasExistingAccess) {
            const data = {
                role: role,
                ...accessData,
            };
            props.updateAdminAccessByAdminId(dialogData._id, data);
        } else {
            handleCreateAccess();
        }
    };

    const renderAccessSelect = (featurePath, currentValue) => {
        const value = currentValue !== undefined ? currentValue : 1;
        return (
            <FormControl size="small" style={{ minWidth: 140 }}>
                <Select
                    value={value}
                    onChange={(e) => handleFeatureChange(featurePath, e.target.value)}
                    className="form-control"
                >
                    <MenuItem value={0} className="text-white">No Access</MenuItem>
                    <MenuItem value={1} className="text-white">Read Only</MenuItem>
                    <MenuItem value={2} className="text-white">Read & Write</MenuItem>
                </Select>
            </FormControl>
        );
    };

    const renderFeatureRow = (label, featurePath) => {
        const value = getNestedValue(accessData, featurePath);
        return (
            <div
                key={featurePath}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
            >
                <span className="text-white">{label}</span>
                {renderAccessSelect(featurePath, value)}
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            aria-labelledby="admin-access-dialog-title"
            onClose={closePopup}
            disableBackdropClick
            disableEscapeKeyDown
            fullWidth
            maxWidth="md"
        >
            <DialogTitle id="admin-access-dialog-title">
                <span className="text-danger font-weight-bold h4">
                    Manage Access - {dialogData?.name || "Admin"}
                </span>
            </DialogTitle>

            <IconButton
                style={{
                    position: "absolute",
                    right: 0,
                }}
            >
                <Tooltip title="Close">
                    <Cancel className="text-danger" onClick={closePopup} />
                </Tooltip>
            </IconButton>

            <DialogContent>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="modal-body pt-1 px-1 pb-3">
                        {/* Role Selection */}
                        <div className="mb-4">
                            <h5 className="mb-3 text-gray">Role</h5>
                            <FormControl fullWidth>
                                <Select
                                    value={role}
                                    onChange={(e) => handleRoleChange(e.target.value)}
                                    className="form-control"
                                >
                                    {ROLES.map((r) => (
                                        <MenuItem key={r} value={r} className="text-white">
                                            {r}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {/* Nested Features */}
                        {Object.entries(FEATURE_STRUCTURE).map(([category, { label, features }]) => (
                            <Accordion key={category} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography className="font-weight-bold text-white">{label}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className="w-100">
                                        {Object.entries(features).map(([featureKey, featureLabel]) =>
                                            renderFeatureRow(featureLabel, `${category}.${featureKey}`)
                                        )}
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {/* Simple Features */}
                        <Accordion defaultExpanded={false}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography className="font-weight-bold text-white">Other Features</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div className="w-100">
                                    {Object.entries(SIMPLE_FEATURES).map(([featureKey, featureLabel]) =>
                                        renderFeatureRow(featureLabel, featureKey)
                                    )}
                                </div>
                            </AccordionDetails>
                        </Accordion>

                        {/* Action Buttons */}
                        <div className="mt-4 d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn btn-outline-info mr-2"
                                onClick={closePopup}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleSaveAll}
                            >
                                {hasExistingAccess ? "Save Changes" : "Create Access"}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default connect(null, {
    getAdminAccessByAdminId,
    createAdminAccess,
    updateAdminAccessByAdminId,
    updateFeatureAccess,
    updateAdminRole,
})(AdminAccessDialog);
